import React, { useEffect, useMemo, useState } from 'react';
import { paymentsApi } from '../../utils/paymentsStore';
import { CLAIM_STATUSES, STATUS_LABELS, UNITS_PER_HOUR, daysBetween, parseCents } from '../../utils/claimMath';
import { Icon, fmtMoney, fmtNum, fmtDate } from '../ui';

const SERVICE_TYPES = ['Personal Care', 'Respite', 'Homemaker', 'Companion', 'Chore', 'Other'];

const blank = {
  clientId: '', patientName: '', patientId: '', payerId: '', payerName: '', serviceType: 'Personal Care',
  dosStart: '', dosEnd: '', datePaid: '', unitsBilled: '', unitsPaid: '', amountBilled: '', amountPaid: '',
  claimNumber: '', status: 'pending', statusNote: '',
};

export default function ClaimDrawer({ claim, clients, payers, defaults, onClose, onSaved, onDeleted, showToast }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEdit = !!claim;

  useEffect(() => {
    if (claim) {
      setForm({
        ...blank, ...claim,
        unitsBilled: claim.unitsBilled || '', unitsPaid: claim.unitsPaid || '',
        amountBilled: claim.amountBilledCents ? (claim.amountBilledCents / 100).toFixed(2) : '',
        amountPaid: claim.amountPaidCents ? (claim.amountPaidCents / 100).toFixed(2) : '',
        clientId: claim.clientId || '', payerId: claim.payerId || '',
      });
    } else {
      const c = defaults?.clientId ? clients.find(x => x.clientId === defaults.clientId) : null;
      setForm({ ...blank, clientId: c?.clientId || '', patientName: c?.clientName || '', patientId: c?.medicaidId || '' });
    }
    setError(''); setConfirmDelete(false);
  }, [claim, defaults, clients]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onClient = (id) => {
    const c = clients.find(x => x.clientId === id);
    setForm(f => ({ ...f, clientId: id, patientName: c ? c.clientName : f.patientName, patientId: c ? (c.medicaidId || f.patientId) : f.patientId }));
  };
  const onPayer = (id) => { const p = payers.find(x => x.payerId === id); setForm(f => ({ ...f, payerId: id, payerName: p ? p.name : f.payerName })); };

  const derived = useMemo(() => {
    const billed = parseCents(form.amountBilled), paid = parseCents(form.amountPaid);
    const units = Number(form.unitsPaid) || 0;
    return {
      hours: units / UNITS_PER_HOUR,
      adjustment: billed - paid,
      lag: form.datePaid ? daysBetween(form.dosEnd || form.dosStart, form.datePaid) : null,
      rate: Number(form.unitsBilled) && billed ? billed / Number(form.unitsBilled) : null,
    };
  }, [form]);

  useEffect(() => {
    // Nudge status to match the money entered, but never override an explicit denial or appeal.
    if (isEdit || form.status === 'denied' || form.status === 'appealed') return;
    const billed = parseCents(form.amountBilled), paid = parseCents(form.amountPaid);
    if (paid > 0 && paid < billed) set('status', 'partial_paid');
    else if (paid > 0 && paid >= billed) set('status', 'paid');
  }, [form.amountBilled, form.amountPaid, form.status, isEdit]);

  const validate = () => {
    if (!form.patientName.trim()) return 'Patient is required';
    if (!form.payerId && !form.payerName.trim()) return 'Insurance company is required';
    if (!form.dosStart) return 'Service start date is required';
    if (form.dosEnd && form.dosEnd < form.dosStart) return 'Service end must be on or after the start';
    if (!parseCents(form.amountBilled)) return 'Amount billed is required';
    if (form.datePaid && form.datePaid < form.dosStart) return 'Date paid cannot be before the service start';
    if ((form.status === 'denied' || form.status === 'appealed') && !form.statusNote.trim()) return `Add a note explaining why the claim is ${STATUS_LABELS[form.status].toLowerCase()}`;
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(); if (v) { setError(v); return; }
    setSaving(true); setError('');
    try {
      const saved = isEdit ? await paymentsApi.updateClaim(claim.claimId, form) : await paymentsApi.createClaim(form);
      showToast(isEdit ? 'Claim updated' : 'Claim added');
      onSaved(saved);
    } catch (err) { setError(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    try { await paymentsApi.deleteClaim(claim.claimId); showToast('Claim deleted'); onDeleted(claim); }
    catch (err) { setError(err.message || 'Delete failed'); }
  };

  return (
    <>
      <div className="ops-drawer__backdrop" onClick={onClose} />
      <aside className="ops-drawer" role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit claim' : 'New claim'}>
        <form onSubmit={submit} style={{ display: 'contents' }}>
          <div className="ops-drawer__head">
            <div><h2>{isEdit ? 'Edit claim' : 'New claim'}</h2>{isEdit && <div className="ops-cell-sub">{claim.claimNumber ? `Claim # ${claim.claimNumber}` : 'No claim # yet'} · {claim.patientName} · {claim.payerName} · {fmtDate(claim.dosStart)} to {fmtDate(claim.dosEnd)} · {claim.source === 'import' ? 'imported' : claim.source === 'sample' ? 'sample data' : 'entered by hand'}</div>}</div>
            <button type="button" className="ops-close" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
          </div>
          <div className="ops-drawer__body">
            {error && <div className="ops-error">{error}</div>}

            <section className="ops-form-section">
              <h3>Service</h3>
              <div className="ops-form-grid">
                <label className="ops-field span-2">Patient (from Clients)
                  <select className="ops-select" value={form.clientId} onChange={e => onClient(e.target.value)}>
                    <option value="">Not linked to a client record</option>
                    {clients.map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}{c.serviceStatus === 'service-ended' ? ' (service ended)' : ''}</option>)}
                  </select>
                </label>
                <label className="ops-field">Patient name<input className="ops-input" value={form.patientName} onChange={e => set('patientName', e.target.value)} required /></label>
                <label className="ops-field">Patient ID<input className="ops-input" value={form.patientId} onChange={e => set('patientId', e.target.value)} placeholder="Medicaid or member ID" /></label>
                <label className="ops-field">Insurance company
                  <select className="ops-select" value={form.payerId} onChange={e => onPayer(e.target.value)}>
                    <option value="">Select a payer</option>
                    {payers.filter(p => p.active !== false).map(p => <option key={p.payerId} value={p.payerId}>{p.name}</option>)}
                  </select>
                </label>
                <label className="ops-field">Service type
                  <select className="ops-select" value={form.serviceType} onChange={e => set('serviceType', e.target.value)}>
                    {SERVICE_TYPES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </label>
                <label className="ops-field">Service start<input className="ops-input" type="date" value={form.dosStart} onChange={e => set('dosStart', e.target.value)} required /></label>
                <label className="ops-field">Service end<input className="ops-input" type="date" value={form.dosEnd} min={form.dosStart || undefined} onChange={e => set('dosEnd', e.target.value)} /></label>
              </div>
            </section>

            <section className="ops-form-section">
              <h3>Billing</h3>
              <div className="ops-form-grid">
                <label className="ops-field">Claim #<input className="ops-input" value={form.claimNumber} onChange={e => set('claimNumber', e.target.value)} placeholder="From the remittance" /></label>
                <div className="ops-field"><span className="ops-label">Rate per unit</span><div className="ops-readout">{derived.rate != null ? fmtMoney(derived.rate) : 'Enter units and amount'}</div></div>
                <label className="ops-field">Units billed<input className="ops-input" type="number" min="0" step="1" value={form.unitsBilled} onChange={e => set('unitsBilled', e.target.value)} /></label>
                <label className="ops-field">Amount billed<input className="ops-input" inputMode="decimal" value={form.amountBilled} onChange={e => set('amountBilled', e.target.value)} placeholder="0.00" required /></label>
              </div>
            </section>

            <section className="ops-form-section">
              <h3>Payment</h3>
              <div className="ops-form-grid">
                <label className="ops-field">Date paid<input className="ops-input" type="date" value={form.datePaid} onChange={e => set('datePaid', e.target.value)} /></label>
                <label className="ops-field">Status
                  <select className="ops-select" value={form.status} onChange={e => set('status', e.target.value)}>
                    {CLAIM_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                </label>
                <label className="ops-field">Units paid<input className="ops-input" type="number" min="0" step="1" value={form.unitsPaid} onChange={e => set('unitsPaid', e.target.value)} /></label>
                <label className="ops-field">Amount paid<input className="ops-input" inputMode="decimal" value={form.amountPaid} onChange={e => set('amountPaid', e.target.value)} placeholder="0.00" /></label>
                <div className="ops-field"><span className="ops-label">Hours of service paid</span><div className="ops-readout">{fmtNum(derived.hours, 2)} h <span className="muted">(units / {UNITS_PER_HOUR})</span></div></div>
                <div className="ops-field"><span className="ops-label">Adjustment</span><div className={`ops-readout${derived.adjustment > 0 ? ' neg' : ''}`}>{fmtMoney(derived.adjustment)} <span className="muted">(billed minus paid)</span></div></div>
                <div className="ops-field"><span className="ops-label">Payment lag</span><div className="ops-readout">{derived.lag != null ? `${derived.lag} days` : 'Not paid yet'}</div></div>
                {(form.status === 'denied' || form.status === 'appealed' || form.statusNote) && (
                  <label className="ops-field span-2">{form.status === 'denied' ? 'Denial reason' : form.status === 'appealed' ? 'Appeal note' : 'Note'}
                    <textarea className="ops-input" rows={3} value={form.statusNote} onChange={e => set('statusNote', e.target.value)} />
                  </label>
                )}
              </div>
            </section>
          </div>
          <div className="ops-drawer__foot">
            {isEdit ? (
              confirmDelete ? (
                <span className="ops-actions"><span className="ops-cell-sub">Delete this claim?</span><button type="button" className="ops-btn ops-btn--danger ops-btn--sm" onClick={remove}>Yes, delete</button><button type="button" className="ops-btn ops-btn--sm" onClick={() => setConfirmDelete(false)}>Cancel</button></span>
              ) : <button type="button" className="ops-btn ops-btn--ghost" onClick={() => setConfirmDelete(true)}><Icon name="trash" /> Delete</button>
            ) : <span />}
            <span className="ops-actions">
              <button type="button" className="ops-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="ops-btn ops-btn--primary" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add claim'}</button>
            </span>
          </div>
        </form>
      </aside>
    </>
  );
}
